import { SectionError } from "@/components/shared/SectionError";
import { Skeleton } from "@/components/ui/skeleton";
import ErrorTable, { type ErrorTableColumn } from "../bulk-stock-upload/ErrorTable";

/**
 * The shell of an expanded Logs > Bulk imports row that lists what an applied
 * import changed (Base garments, client prices, a bulk edit): loading
 * skeleton, the load error with Retry, the empty line, then the shared
 * ErrorTable (search and the full-set CSV download) rendering the first
 * SHOWN_ROWS. Each row passes its query, columns and words; the shell owns
 * every state, so the three behave the same.
 *
 * Fixed layout with percentage columns: every cell wraps inside the width it
 * is given (BulkImportsTab pins that to the visible part of the Logs table),
 * so the Export button and the last column never sit past the right edge.
 * Below 36rem the change list scrolls inside its own box.
 */

const SHOWN_ROWS = 500;

/** The part of a react-query result the shell reads. */
export interface ChangesRowQuery<Change> {
  data: readonly Change[] | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => unknown;
}

/** Already-translated words of one row. */
export interface ChangesRowTexts {
  /** Title of the error block when the list did not load. */
  error: string;
  retry: string;
  /** The line when the import changed nothing. */
  empty: string;
  /** The line under the table when more than SHOWN_ROWS changes exist (the CSV has them all). */
  more: (counts: { shown: number; hidden: number }) => string;
}

export interface ChangesRowShellProps<Change> {
  query: ChangesRowQuery<Change>;
  columns: ErrorTableColumn<Change>[];
  rowKey: (change: Change, index: number) => string;
  filename: string;
  searchPlaceholder: string;
  texts: ChangesRowTexts;
}

function ChangesRowShell<Change>({
  query,
  columns,
  rowKey,
  filename,
  searchPlaceholder,
  texts,
}: ChangesRowShellProps<Change>) {
  if (query.isLoading) {
    return (
      <div aria-busy="true" className="space-y-2 py-2">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-6 w-full" />
        ))}
      </div>
    );
  }

  if (query.error) {
    return (
      <SectionError
        variant="compact"
        title={texts.error}
        onRetry={() => void query.refetch()}
        retryLabel={texts.retry}
        retrying={query.isFetching}
      />
    );
  }

  const changes = query.data ?? [];
  if (changes.length === 0) {
    return <p className="py-4 text-center text-sm text-muted-foreground">{texts.empty}</p>;
  }

  return (
    <div className="space-y-2">
      <ErrorTable<Change>
        errors={changes}
        columns={columns}
        rowKey={rowKey}
        filename={filename}
        maxRows={SHOWN_ROWS}
        searchPlaceholder={searchPlaceholder}
        tableClassName="table-fixed min-w-[36rem]"
      />
      {changes.length > SHOWN_ROWS && (
        <p className="text-center text-xs text-muted-foreground">
          {texts.more({ shown: SHOWN_ROWS, hidden: changes.length - SHOWN_ROWS })}
        </p>
      )}
    </div>
  );
}

export default ChangesRowShell;
